import { type ReactNode } from "react";
import { Pause } from "lucide-react";
import { Button, cn } from "@vseh/ui";

/**
 * AI остановился и ждёт человеческого решения. По §14 «Approval gate».
 *
 * ВАЖНО: кнопка «Подтвердить» — primary, НЕ ai. Это решение человека.
 * AI готовит — человек одобряет. Использование ai-cyan здесь будет
 * сигналом, что AI принял решение сам, что неверно.
 *
 * Цветовая обёртка — heritage (амбер): «требует решения» — ровно то
 * семантическое значение, которое нам нужно.
 */
export interface ApprovalGateProps {
  /** Заголовок — что AI готов сделать. Не вопрос, а утверждение. */
  title: ReactNode;
  /** Сводка деталей. Список ключ-значений или произвольный контент. */
  details?: ReactNode;
  /** Контекст риска — поясняет, почему это требует подтверждения. */
  risk?: ReactNode;
  primaryLabel?: string;
  secondaryLabel?: string;
  destructiveLabel?: string;
  loading?: "primary" | "secondary" | "destructive" | null;
  onPrimary?: () => void;
  onSecondary?: () => void;
  onDestructive?: () => void;
  className?: string;
}

export function ApprovalGate({
  title,
  details,
  risk,
  primaryLabel = "Подтвердить",
  secondaryLabel,
  destructiveLabel = "Отклонить",
  loading = null,
  onPrimary,
  onSecondary,
  onDestructive,
  className,
}: ApprovalGateProps): JSX.Element {
  return (
    <div
      role="alertdialog"
      aria-labelledby="approval-gate-title"
      className={cn(
        "rounded-lg border-l-4 border-heritage-500 bg-heritage-50 p-4",
        "text-base-900",
        className,
      )}
    >
      <div className="mb-3 flex items-start gap-3">
        <Pause
          aria-hidden="true"
          className="mt-0.5 size-5 shrink-0 text-heritage-700"
        />
        <div className="min-w-0 flex-1">
          <div
            id="approval-gate-title"
            className="font-display text-lg font-semibold"
          >
            {title}
          </div>
          {details && <div className="mt-2 text-sm text-base-700">{details}</div>}
          {risk && (
            <div className="mt-2 text-xs text-heritage-800">
              <span className="font-medium">Почему требуется:</span> {risk}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pl-8">
        <Button
          variant="primary"
          loading={loading === "primary"}
          onClick={onPrimary}
        >
          {primaryLabel}
        </Button>
        {secondaryLabel && (
          <Button
            variant="secondary"
            loading={loading === "secondary"}
            onClick={onSecondary}
          >
            {secondaryLabel}
          </Button>
        )}
        <Button
          variant="ghost"
          loading={loading === "destructive"}
          onClick={onDestructive}
        >
          {destructiveLabel}
        </Button>
      </div>
    </div>
  );
}
